// Loading models and panorama images from URLs or local files.
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { panoTextureFrom } from './engine';

export type ModelFormat = 'glb' | 'gltf' | 'obj';

export function modelFormatOf(name: string): ModelFormat | null {
  const ext = name.split('.').pop()?.toLowerCase();
  return ext === 'glb' || ext === 'gltf' || ext === 'obj' ? ext : null;
}

/** Parses a model from raw data. */
export async function parseModel(data: ArrayBuffer | string, format: ModelFormat): Promise<THREE.Object3D> {
  if (format === 'obj') {
    const text = typeof data === 'string' ? data : new TextDecoder().decode(data);
    return new OBJLoader().parse(text);
  }
  return new Promise((resolve, reject) => new GLTFLoader().parse(data, '', (g) => resolve(g.scene), reject));
}

/** Loads a model from a URL. */
export async function loadModel(url: string, format: ModelFormat): Promise<THREE.Object3D> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Model failed to load (${res.status})`);
  return parseModel(format === 'obj' ? await res.text() : await res.arrayBuffer(), format);
}

/** Loads a model from a dropped or picked file. */
export async function loadModelFile(file: File): Promise<THREE.Object3D> {
  const format = modelFormatOf(file.name);
  if (!format) throw new Error('Use a .glb, .gltf or .obj model');
  return parseModel(format === 'glb' ? await file.arrayBuffer() : await file.text(), format);
}

/** Decodes an image (URL or Blob) into a bitmap. */
export async function decodeImage(src: string | Blob): Promise<ImageBitmap> {
  const blob = typeof src === 'string' ? await (await fetch(src)).blob() : src;
  return createImageBitmap(blob);
}

/** Loads a pano image as a texture, at most `maxWidth` wide. Returns the texture and the source width. */
export async function loadPanoTexture(src: string | Blob, maxWidth: number, renderer?: THREE.WebGLRenderer): Promise<{ texture: THREE.Texture; sourceWidth: number; aspect: number; bitmap: ImageBitmap }> {
  const bmp = await decodeImage(src);
  const w = Math.min(bmp.width, maxWidth, renderer?.capabilities.maxTextureSize ?? maxWidth);
  const texture = panoTextureFrom(bmp, w, renderer);
  return { texture, sourceWidth: bmp.width, aspect: bmp.width / bmp.height, bitmap: bmp };
}
